import { QUERYSTRING_IFRAMEURL } from '../constants/queryString';
import type { MenuItem } from '../schemas/menuItemSchema';

const MYPAGES_NETWORK_URL = '/mypages/network';
const MEMBER_CONSULTANT_TYPE = 11;

/* Find position for merging */
const findSameNodeIndex = (currentMenu: MenuItem[], newMenuNode: MenuItem): number =>
  currentMenu.findIndex(
    (currentMenuNode: MenuItem) =>
      newMenuNode.title &&
      currentMenuNode.title &&
      newMenuNode.title.trim().toLowerCase() === currentMenuNode.title.trim().toLowerCase(),
  );

const setupSubItemsItemPaths = (itemPath: string, menuItems: MenuItem[]): void => {
  for (const [i, subItem] of menuItems.entries()) {
    subItem.itemPath = `${itemPath}/${i}/`;

    if (subItem.subItems.length > 0) {
      setupSubItemsItemPaths(subItem.itemPath, subItem.subItems);
    }
  }
};

/* Merge new node to existings menu, recursive */
const mergeToNode = (
  currentMenu: MenuItem[],
  newMenu: MenuItem[],
  parentItemPath: string,
  rootItemUrl: string | null,
  consultantType?: number,
  reportsItemName?: string,
): void => {
  for (const newMenuNode of newMenu) {
    const shouldSkipForMember =
      consultantType === MEMBER_CONSULTANT_TYPE &&
      rootItemUrl === MYPAGES_NETWORK_URL &&
      newMenuNode.title === reportsItemName;

    if (shouldSkipForMember) {
      continue;
    }

    const relatedNodeIndex = findSameNodeIndex(currentMenu, newMenuNode);

    if (relatedNodeIndex !== -1) {
      // needs to merge, subitems
      if (newMenuNode.subItems.length > 0) {
        const parentNode = currentMenu[relatedNodeIndex];

        if (parentNode !== undefined) {
          const { subItems, itemPath } = parentNode;
          if (subItems.length > 0) {
            mergeToNode(subItems, newMenuNode.subItems, itemPath, rootItemUrl, consultantType, reportsItemName);
          }
        }
      }
    } else if (newMenuNode.subItems.length > 0 || newMenuNode.url) {
      const itemPath = `${parentItemPath}/${currentMenu.length}/`;

      newMenuNode.itemPath = itemPath;

      if (newMenuNode.subItems.length > 0) {
        setupSubItemsItemPaths(itemPath, newMenuNode.subItems);
      }
      // needs to add, subitems
      currentMenu.push(newMenuNode);
    }
  }
};

export const mergeOrisalesWithPersonalMenuItems = (
  personalMenuItems: MenuItem[],
  orisalesMenuItems: MenuItem[],
  consultantType?: number,
  reportsItemName?: string,
): MenuItem[] => {
  const result = [...personalMenuItems];

  if (!orisalesMenuItems.length) {
    return result;
  }

  // Merge root items
  for (const newMenuNode of orisalesMenuItems) {
    const relatedRootIndex = findSameNodeIndex(result, newMenuNode);

    if (relatedRootIndex !== -1) {
      const parentNode = result[relatedRootIndex];

      if (parentNode !== undefined) {
        const parentUrl = parentNode.url;

        // modify eshop iframe links - also in all subitems
        let rootItemStr = JSON.stringify(newMenuNode);
        rootItemStr = rootItemStr.replaceAll(/"url":"(.*?)"/g, (_: string, url: string): string => {
          const encodedParam = encodeURIComponent(url);

          return `"url":"${parentUrl}?${QUERYSTRING_IFRAMEURL}=${encodedParam}"`;
        });

        const adjustedNode = JSON.parse(rootItemStr) as MenuItem;

        // Merge both menus
        const { subItems, itemPath } = parentNode;
        if (relatedRootIndex !== -1 && adjustedNode.subItems.length > 0 && subItems.length > 0) {
          mergeToNode(subItems, adjustedNode.subItems, itemPath, parentUrl ?? null, consultantType, reportsItemName);
        }
      }
    }
  }

  return result;
};
